import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import { Box, Button, Container, Dialog, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCityByState, getCurrentDateTime, getKeyPathValue, getStatesByCountry, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { INV_BALANCE_TYPE, BALANCING_METHOD, TRADE_MODE, DEALER_TYPE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import AddIcon from '@mui/icons-material/Add';
import { PayTermsForm } from './PayTermsForm';

const TABLE_HEADER = [
    {
        header: 'Ledger Name',
        accessor: (row) => {
            const status = row.status;
            return (
                <span
                    style={{
                        color: status === 'inactive' ? 'red' : 'black',
                    }}
                >
                    {`${row.ledger_name}`}
                </span>
            );
        },
    },
    {
        header: 'Print Name',
        accessor: 'print_name'
    },
    {
        header: 'Group Name',
        accessor: (row) => row?.groupname?.groupname
    },
    {
        header: 'Opening Balance',
        accessor: 'opening_balance'
    },
    {
        header: 'Balance Type',
        accessor: 'balance_type'
    },
    {
        header: 'Created At',
        accessor: 'created_at'
    },
    {
        header: 'Status',
        accessor: 'status',
        sx: { textTransform: 'uppercase' }
    }
];


const initialAccountLedgerDataObj = {
    data: [],
    loading: false
};

export const AccountLedger = () => {
    const { pathname } = useLocation();
    const lastPathSegment = pathname.split('/').pop();
    const [detailList, setDetailList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentAction, setCurrentAction] = useState();
    const [selectedRecord, setSelectedRecord] = useState();
    const [accountLedgersDataObj, setAccountLedgersDataObj] = useState(initialAccountLedgerDataObj);
    const [openQueryDialog, setOpenQueryDialog] = useState(false);
    const [patientDetailList, setPatientDetailList] = useState([]);
    const [groupdropdown, setGroupdropdown] = useState([])
    const [activeTab, setActiveTab] = useState(0)
    const [addPayTermsOpen, setAddPayTermsOpen] = useState(false)
    const [payTerms, setPayTerms] = useState([])

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                ledger_name: Yup.string().required(' '),
            })
        ),
        defaultValues: {
            country: 'IN'
        },
        mode: 'all'
    });
    const { handleSubmit, watch, reset, setValue } = methods;

    useEffect(() => {
        if (selectedRecord) {
            const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'status', '__v']);
            keyValuePair.forEach(({ key, value }) => setValue(key, value));
        } else {
            reset();
        }
    }, [setValue, reset, selectedRecord]);

    const formSubmit = async (formValues) => {
        setLoading(true);
        let editFlag = false;
        if (selectedRecord && currentAction === 'edit') {
            editFlag = true;
        }
        const response = await request(editFlag ? '/account-ledger-update' : '/account-ledger-create', {
            method: editFlag ? 'PUT' : 'POST',
            params: editFlag ? selectedRecord._id : null,
            body: formValues
        });
        if (response?.success) {
            toast(`Successfully ${editFlag ? 'edited' : 'created'} Account Legder`, { variant: 'success' });
            setSelectedRecord(response.data);
            setCurrentAction();
        } else {
            toast(`Failed to ${editFlag ? 'edit' : 'create'} Account Ledger`, { variant: 'error' });
        }
        setLoading(false);
    };


    const handleCancelRecord = async () => {
        setLoading(true);
        const response = await request('/account-ledger-cancel', {
            method: 'DELETE',
            params: selectedRecord._id
        });
        if (response?.success) {
            toast('Successfully Cancelled Account Ledger', { variant: 'success' });
            setSelectedRecord();
        } else {
            toast('Failed to cancel Account Ledger', { variant: 'error' });
        }
        setLoading(false);
    };


    const handleDeleteRecord = async () => {
        setLoading(true);
        const response = await request('/account-ledger-delete', {
            method: 'DELETE',
            params: selectedRecord._id
        });
        if (response?.success) {
            toast('Successfully deleted Account Ledger', { variant: 'success' });
            setSelectedRecord();
        } else {
            toast('Failed to delete Account Legder', { variant: 'error' });
        }
        setLoading(false);
    };

    const handleQueryWindow = async () => {
        setOpenQueryDialog(true);
        setAccountLedgersDataObj({ loading: true });
        const response = await request('/account-ledger-list', { method: 'GET' });
        if (response.success) {
            const filteredData = response.data.filter(item =>
                item.status === 'active' || item.status === 'inactive' || item.status === 'default'
            );
            setAccountLedgersDataObj({ loading: false, data: filteredData });
        } else {
            toast('Error in fetching data', { variant: 'error' });
            setAccountLedgersDataObj({ loading: false });
        }
    };


    const getGroupDropdown = useCallback(async () => {
        const data = await dropdownFetch('account-groups');
        setGroupdropdown(data);
    }, []);

    const getPayTermDropdown = useCallback(async () => {
        const data = await dropdownFetch('pay-term');
        setPayTerms(data);
    }, []);

    useEffect(() => {
        getGroupDropdown()
        getPayTermDropdown()
    }, [getGroupDropdown, getPayTermDropdown])

    const handleActiveTab = (newTabIndex) => {
        setActiveTab(newTabIndex);
    };
    return (
        <>
            <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
                <Container
                    sx={{
                        mt: 1,
                        border: '0px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        width: '100%',
                        maxWidth: 'none',
                        minWidth: "100%",
                        boxSizing: 'border-box',
                    }}
                >
                    <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                mb: 1,
                                borderBottom: '0px solid',
                                borderColor: 'grey.300'
                            }}
                        >
                            <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                                Account Ledger
                            </Typography>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                                <CustomButtonn
                                    id={`${lastPathSegment}Write`}
                                    variant="contained"
                                    disabled={['edit', 'new'].includes(currentAction)}
                                    onClick={() => {
                                        setSelectedRecord();
                                        setCurrentAction('new');
                                        setPatientDetailList([])
                                    }}
                                >
                                    New
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`${lastPathSegment}Update`}
                                    variant="contained"
                                    disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                                    onClick={() => setCurrentAction('edit')}
                                >
                                    Edit
                                </CustomButtonn>
                                <CustomButtonn
                                    id={"Submit"}
                                    type="submit"
                                    variant="contained"
                                    disabled={!['edit', 'new'].includes(currentAction) || loading}
                                >
                                    Save
                                </CustomButtonn>
                                <CustomButtonn
                                    id={"Cancel"}
                                    variant="contained"
                                    disabled={['edit', 'new'].includes(currentAction) || loading}
                                    onClick={() => {
                                        setCurrentAction();
                                        handleCancelRecord()
                                        reset();
                                    }}
                                >
                                    Cancel
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`isSuperAdmin`}
                                    variant="contained"
                                    disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                                    onClick={handleDeleteRecord}
                                >
                                    Delete
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`${lastPathSegment}Read`}
                                    variant="contained"
                                    onClick={handleQueryWindow}
                                    disabled={['new', 'edit'].includes(currentAction)}
                                >
                                    Query
                                </CustomButtonn>
                                <CustomButtonn
                                    id={`${lastPathSegment}Print`}
                                    variant="contained"
                                    onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                                    disabled={!selectedRecord || currentAction}
                                >
                                    Print
                                </CustomButtonn>
                                <CustomButtonn id={"Exit"} variant="contained"
                                    onClick={() => {
                                        setCurrentAction('');
                                    }}
                                >Exit</CustomButtonn>
                            </Stack>
                        </Stack>
                        <Box sx={{ border: '0px solid gray', padding: 2, borderRadius: '8px', width: '80%',boxShadow: '0px 2px 10px #0099ff'  }}>
                        <Grid container spacing={2}>
                            <Grid item xs={8} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                <Stack spacing={0.5} direction="row" alignItems="center">
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Ledger Name</Typography>
                                    <TextField sx={{ flex: 0.8, border: currentAction ? "0.5px solid red" : ""}} name="ledger_name" disabled={!['new', 'edit'].includes(currentAction)} placeholder="Enter Ledger Name "

                                    />
                                </Stack>
                                <Stack spacing={0.5} direction="row" alignItems="center">
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Print Name</Typography>
                                    <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="print_name" placeholder="Enter Print Name" />
                                </Stack>
                                <Stack spacing={0.5} direction="row" alignItems="center">
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Opening Balance</Typography>
                                    <TextField
                                        sx={{ flex: 0.3 }}
                                        name="opening_balance"
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                    />
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}> Balance Type</Typography>
                                    <SelectTag name="balance_type" disabled={!['new', 'edit'].includes(currentAction)} formControlSX={{ flex: 0.3 }} menus={INV_BALANCE_TYPE} />
                                </Stack>


                            </Grid>
                            <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column'}}>
                                <Stack spacing={0.5} direction="row" alignItems="center">
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Alias</Typography>
                                    <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} placeholder="Enter Alias Name" name="alias_name" />
                                </Stack>
                                <Stack spacing={0.5} direction="row" alignItems="center">
                                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Group</Typography>
                                    <SelectTag
                                        name="groupname"
                                        disabled={!['new', 'edit'].includes(currentAction)}
                                        formControlSX={{ flex: 0.8, border: currentAction ? "0.5px solid red" : "" }}
                                        menus={groupdropdown.map((data) => ({
                                            ...data, name: data.
                                                groupname
                                        }))}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', marginBottom: "1%",boxShadow: '0px 2px 2px #0099ff' }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleActiveTab(0)}
                                        sx={{
                                            my: 0,
                                            mx: 0,
                                            borderRadius: '4px',
                                            fontWeight: 700,
                                        }} >General Information</Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleActiveTab(1)}
                                        sx={{
                                            my: 0,
                                            mx: 0.5,
                                            borderRadius: '4px',
                                            fontWeight: 700,
                                        }} >Taxation</Button>
                                </Box>
                            </Grid>
                        </Grid>
                        

                        {activeTab === 0 && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Method</Typography>
                                            <SelectTag formControlSX={{ flex: 0.8, border: currentAction ? "0.5px solid red" : "" }} name="method" disabled={!['new', 'edit'].includes(currentAction)} placeholder="Select Balancing Method"
                                                menus={BALANCING_METHOD}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.23, fontWeight: 700, fontSize: '1.4rem' }}>Pay Term</Typography>
                                            <SelectTag disabled={!['new', 'edit'].includes(currentAction)} formControlSX={{ flex: 0.8, border: currentAction ? "0.5px solid red" : "" }}
                                                name="pay_term" placeholder="Select Pay Term"
                                                menus={payTerms.map((data) => ({ ...data, name: data.pay_term }))}
                                            />
                                            <Button
                                                sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }}
                                                variant="contained"
                                                onClick={() => setAddPayTermsOpen(true)}
                                            >
                                                <AddIcon />
                                            </Button>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Trade Mode</Typography>
                                            <SelectTag
                                                name="trade_mode"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                formControlSX={{ flex: 0.8 , border: currentAction ? "0.5px solid red" : ""}}
                                                placeholder="Select Trade Mode"
                                                menus={TRADE_MODE}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column',boxShadow: '0px 2px 2px #0099ff' }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '1.4rem',                                           
                                        }}
                                    >
                                        Address Information
                                    </Typography>
                                    </Grid>
                                   
                                </Grid>
                                <Grid container spacing={2} marginTop={"1%"}>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Country</Typography>
                                            <SelectTag
                                                formControlSX={{
                                                    flex: 0.8,
                                                    border: currentAction ? "0.5px solid red" : ""}}
                                                name="country"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                placeholder="Select Country"
                                                menus={[{ _id: 'IN', name: 'India' }]}
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>City</Typography>
                                            <SelectTag disabled={!['new', 'edit'].includes(currentAction)} formControlSX={{ flex: 0.8 }} name="city" placeholder="Select City"
                                                menus={getCityByState(watch('country'), watch('state'))}
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Mobile</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="mobile" placeholder="Enter Mobile" />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Phone</Typography>
                                            <TextField
                                                name="phone_no"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                sx={{ flex: 0.8 }}
                                                placeholder="Enter Phone No."
                                            />
                                        </Stack>


                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>State</Typography>
                                            <SelectTag disabled={!['new', 'edit'].includes(currentAction)} formControlSX={{ flex: 0.8 }} name="state" placeholder="Select State"
                                                menus={getStatesByCountry(watch('country'))}
                                            />

                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Address</Typography>
                                            <TextField
                                                name="address"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                sx={{ flex: 0.8 }}
                                                placeholder="Enter Address 1"
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Zip Code</Typography>
                                            <TextField
                                                name="zip_code"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                sx={{ flex: 0.8 }}
                                                placeholder="Enter Zip Code"
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Cont. Person</Typography>
                                            <TextField
                                                name="contact_person"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                sx={{ flex: 0.8 }}
                                                placeholder="Enter Contact Person"
                                            />
                                        </Stack>

                                        
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>GST Code</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="gst_code" />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Email</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="email"
                                                placeholder="Enter Email"
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Designation</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="designation"
                                                placeholder="Enter Designation"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {activeTab === 1 && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Dealer Type</Typography>
                                            <SelectTag formControlSX={{ flex: 0.8, }} name="dealer_type" disabled={!['new', 'edit'].includes(currentAction)} placeholder="Select Dealer Type"
                                                menus={DEALER_TYPE}

                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>CIN</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="cin" placeholder="Enter CIN" />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>PAN No.</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="pan_no" placeholder="Enter PAN No." />

                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>DL Number</Typography>
                                            <TextField
                                                name="dl_no"
                                                disabled={!['new', 'edit'].includes(currentAction)}
                                                sx={{ flex: 0.8 }}
                                                placeholder="Enter Dl Number"
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={4} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>GST No.</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="gst_no"
                                                placeholder="Enter GST No."
                                            />
                                        </Stack>
                                        <Stack spacing={0.5} direction="row" alignItems="center">
                                            <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Aadhar  No.</Typography>
                                            <TextField disabled={!['new', 'edit'].includes(currentAction)} sx={{ flex: 0.8 }} name="aadhar_no"
                                                placeholder="Enter Aadhar No."
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                        </Box>
                    </FormProvider>
                </Container>
            </Paper>
            <Dialog
                open={openQueryDialog}
                onClose={() => setOpenQueryDialog(false)}
                PaperProps={{ sx: { width: '80%', p: 2 } }}
                maxWidth="xl"
                scroll="body"
            >
                <Stack
                    padding={0}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    borderBottom="1px solid"
                    borderColor="grey.300"
                >
                    <Typography variant="h4" fontWeight="bold">
                        Account Ledger Query Window
                    </Typography>
                    <IconButton onClick={() => setOpenQueryDialog(false)}>
                        <CloseIcon color="error" fontSize="large" />
                    </IconButton>
                </Stack>
                <Table
                    sx={{ mt: 0.5 }}
                    columns={TABLE_HEADER}
                    data={accountLedgersDataObj.data || []}
                    loading={accountLedgersDataObj.loading}
                    onRowClick={(row) => {
                        const {
                            groupname,
                            ...otherDetails
                        } = row;
                        setSelectedRecord({
                            ...otherDetails,
                            groupname: groupname?._id,
                        });
                        setOpenQueryDialog(false);
                    }}
                />
            </Dialog>


            <Dialog
                open={addPayTermsOpen}
                onClose={() => setAddPayTermsOpen(false)}
                PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
                maxWidth="xl"
                scroll="body"
            >
                <PayTermsForm
                    onClose={() => setAddPayTermsOpen(false)}
                    setAddPayTermsOpen={setAddPayTermsOpen}
                    getPayTermDropdown={getPayTermDropdown}
                />
            </Dialog>
        </>
    );
};
